import { useState, useEffect } from "react";
import { Camera } from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_ENDPOINTS } from "../../../constants/config";
import { toast } from "react-hot-toast";
// Import the error handler utility
import { extractErrorMessage } from "../../../utils/error-handler";

const EditProfileModal = ({ isOpen, onClose, student, onUpdate }) => {
  const [formData, setFormData] = useState({});
  const [classData, setClassData] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Real-time validation function
  const validateField = (name, value) => {
    const errors = { ...fieldErrors };
    
    switch (name) {
      case "firstName":
        if (value && value.trim() !== "") {
          if (value.length < 2) {
            errors.firstName = "First name must be at least 2 characters long";
          } else {
            delete errors.firstName;
          }
        } else {
          delete errors.firstName;
        }
        break;
        
      case "lastName":
        if (value && value.trim() !== "") {
          if (value.length < 2) {
            errors.lastName = "Last name must be at least 2 characters long";
          } else {
            delete errors.lastName;
          }
        } else {
          delete errors.lastName;
        }
        break;
        
      case "email":
        if (value && value.trim() !== "") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors.email = "Please enter a valid email address";
          } else {
            delete errors.email;
          }
        } else {
          delete errors.email;
        }
        break;
        
      // Phone number validation removed as there's no phone number field in the form
      case "mobileNumber":
        // No validation needed for mobile number as there's no field in the form
        break;
        
      case "fatherContactNumber":
        if (value && value.trim() !== "") {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ""))) {
            errors.fatherContactNumber = "Father's contact number must be exactly 10 digits";
          } else {
            delete errors.fatherContactNumber;
          }
        } else {
          delete errors.fatherContactNumber;
        }
        break;
        
      case "motherContactNumber":
        if (value && value.trim() !== "") {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ""))) {
            errors.motherContactNumber = "Mother's contact number must be exactly 10 digits";
          } else {
            delete errors.motherContactNumber;
          }
        } else {
          delete errors.motherContactNumber;
        }
        break;
        
      case "address.pincode":
        if (value && value.trim() !== "") {
          const pincodeRegex = /^[0-9]{6}$/;
          if (!pincodeRegex.test(value)) {
            errors.pincode = "Pincode must be exactly 6 digits";
          } else {
            delete errors.pincode;
          }
        } else {
          delete errors.pincode;
        }
        break;
        
      case "dateOfBirth":
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 3 || age > 25) {
            errors.dateOfBirth = "Student age must be between 3 and 25 years";
          } else {
            delete errors.dateOfBirth;
          }
        } else {
          delete errors.dateOfBirth;
        }
        break;
        
      // Admission number is optional
      case "admissionNumber":
        // No validation needed for admission number as it's optional
        break;
        
      // Roll number is optional, so no validation required
      case "rollNumber":
        // No validation needed for roll number as it's optional
        break;
        
      // Father's name is optional
      case "fatherName":
        // No validation needed for father's name as it's optional
        break;
        
      // Mother's name is optional
      case "motherName":
        // No validation needed for mother's name as it's optional
        break;
        
      // Blood group is optional
      case "bloodGroup":
        // No validation needed for blood group as it's optional
        break;
        
      default:
        break;
    }
    
    setFieldErrors(errors);
  };

  useEffect(() => {
    const fetchSectionsByClass = async () => {
      if (!formData.class) {
        console.log("No class selected, clearing sections");
        setAvailableSections([]);
        return;
      }

      try {
        console.log("Fetching sections for class:", formData.class);
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(
          API_ENDPOINTS.sectionsByClass(formData.class),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Sections response:", response.data);
        setAvailableSections(response.data.data.sections);
      } catch (error) {
        console.error("Error fetching sections:", error);
        toast.error(
          `Failed to fetch sections: ${
            error.response?.data?.message || error.message
          }`
        );
        setAvailableSections([]);
      }
    };

    fetchSectionsByClass();
  }, [formData.class]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.classes, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClassData(response.data.data.classes);
      } catch (error) {
        toast.error(
          `Failed to fetch classes: ${
            error.response?.data?.message || error.message
          }`
        );
      }
    };

    fetchClasses();
  }, []);

  // Clear field errors when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFieldErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (student) {
      setFormData({
        firstName: student.firstName || "",
        middleName: student.middleName || "",
        lastName: student.lastName || "",
        profileImage: student.profileImage || null,
        email: student.email || "",
        rollNumber: student.rollNumber || "",
        admissionNumber: student.admissionNumber || "",
        aadhaarCard: student.aadhaarCard || "",
        class: student.classMapping?.class?._id || "",
        section: student.classMapping?.section?._id || "",
        dateOfBirth: (student.dateOfBirth || student.birthday)
        ? new Date(student.dateOfBirth || student.birthday).toISOString().split("T")[0]
        : "",
        category: student.category || "",
        mobileNumber: student.mobileNumber || "",
        fatherName: student.fatherName || "",
        fatherContactNumber: student.fatherContactNumber || "",
        motherName: student.motherName || "",
        motherContactNumber: student.motherContactNumber || "",
        guardianName: student.guardianName || "",
        guardianContactNumber: student.guardianContactNumber || "",
        guardianEmail: student.guardianEmail || "",
        medicalHistory: student.medicalHistory || "",
        note: student.note || "",
        bloodGroup: student.bloodGroup || "",
        gender: student.gender || "",
        transportationType: student.transportationType || "",
        address: {
          street: student.address?.street || "",
          city: student.address?.city || "",
          state: student.address?.state || "",
          pincode: student.address?.pincode || "",
          country: student.address?.country || "India",
        },
        status: student.status || "active",
      });
    }
  }, [student]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value,
        },
      }));
      // Validate address fields
      if (addressField === "pincode") {
        validateField("address.pincode", value);
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
    
    // Call real-time validation for the changed field
    validateField(name, value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      profileImage: file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Enhanced frontend validation with specific error messages
      const validationErrors = [];

      // First name validation - only validate length if provided
      if (formData.firstName && formData.firstName.trim() !== "") {
        if (formData.firstName.length < 2) {
          validationErrors.push("First name must be at least 2 characters long");
        }
      }

      // Last name validation - only validate length if provided
      if (formData.lastName && formData.lastName.trim() !== "") {
        if (formData.lastName.length < 2) {
          validationErrors.push("Last name must be at least 2 characters long");
        }
      }

      // Email validation - only validate format if provided
      if (formData.email && formData.email.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          validationErrors.push("Please enter a valid email address");
        }
      }

      // Phone number validation removed as there's no phone number field in the form

      // Admission number validation removed as it might be optional

      // Roll number is optional, so no validation required

      // Father's name validation removed as it might be optional

      // Parent contact number validation - only validate format if provided
      if (formData.fatherContactNumber && formData.fatherContactNumber.trim() !== "") {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.fatherContactNumber.replace(/\s/g, ""))) {
          validationErrors.push("Father's contact number must be exactly 10 digits");
        }
      }

      // Mother's name validation removed as it might be optional

      if (formData.motherContactNumber && formData.motherContactNumber.trim() !== "") {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.motherContactNumber.replace(/\s/g, ""))) {
          validationErrors.push("Mother's contact number must be exactly 10 digits");
        }
      }

      // Birthday validation - only validate age if date is provided
      if (formData.dateOfBirth) {
        const birthDate = new Date(formData.dateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 3 || age > 25) {
          validationErrors.push("Student age must be between 3 and 25 years");
        }
      }

      // Blood group validation removed as it might be optional

      // Address validation - only validate pincode format if provided
      if (formData.address && formData.address.pincode && formData.address.pincode.trim() !== "") {
        const pincodeRegex = /^[0-9]{6}$/;
        if (!pincodeRegex.test(formData.address.pincode)) {
          validationErrors.push("Pincode must be exactly 6 digits");
        }
      }

      // Show frontend validation errors
      if (validationErrors.length > 0) {
        toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`);
        setIsSubmitting(false);
        return;
      }

      // Validate the student ID first
      if (!student || !student._id) {
        toast.error("Invalid student ID. Please try again.");
        setIsSubmitting(false);
        return;
      }

      // Prepare form data correctly
      const formDataToSend = new FormData();

      if (formData.profileImage && typeof formData.profileImage !== "string") {
        formDataToSend.append("file", formData.profileImage);
      }

      // Create a copy of the data to send
      const dataToSend = { ...formData };

      // Handle the transportationType and busRoute relationship
      if (dataToSend.transportationType !== "bus") {
        dataToSend.busRoute = null; // Clear bus route if not using bus transportation
      }

      // Remove any ObjectId wrappers from IDs
      if (dataToSend.busRoute && typeof dataToSend.busRoute === 'object' && dataToSend.busRoute._id) {
        dataToSend.busRoute = dataToSend.busRoute._id;
      }
      if (dataToSend.class && typeof dataToSend.class === 'object' && dataToSend.class._id) {
        dataToSend.class = dataToSend.class._id;
      }
      if (dataToSend.section && typeof dataToSend.section === 'object' && dataToSend.section._id) {
        dataToSend.section = dataToSend.section._id;
      }

      delete dataToSend.profileImage;
      formDataToSend.append("data", JSON.stringify(dataToSend));

      const token = localStorage.getItem("token");
      const response = await axiosInstance.put(
        API_ENDPOINTS.user(student._id),
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Update response:", response.data);
      toast.success("Student information has been updated successfully.");
      
      // Close modal and call onUpdate
      onClose();
      onUpdate?.(student._id, dataToSend);
      
      // Refresh the page automatically
      window.location.reload();
      
    } catch (error) {
      console.error("Failed to update student:", error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to update student: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex h-[100dvh] w-[100vw] max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100vw-2rem)] sm:max-w-4xl sm:rounded-lg sm:border">
        <DialogHeader className="shrink-0 border-b px-4 py-4 sm:px-6">
          <DialogTitle>Edit Student Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <div className="space-y-4">
              <div className="mb-2 flex justify-center">
                <div className="relative w-28 sm:w-32">
                  <div className="group aspect-square w-full overflow-hidden rounded-full border-2 border-gray-200">
                {formData.profileImage ? (
                  <img
                    src={
                      typeof formData.profileImage === "string"
                        ? formData.profileImage
                        : URL.createObjectURL(formData.profileImage)
                    }
                    alt="Profile Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <svg
                      className="w-12 h-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                  <Camera className="w-8 h-8 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div>
              <label className="text-sm font-medium">First Name *</label>
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
              {fieldErrors.firstName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Middle Name</label>
              <Input
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Last Name</label>
              <Input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
              {fieldErrors.lastName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.lastName}</p>
              )}
            </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Roll Number</label>
              <Input
                name="rollNumber"
                value={formData.rollNumber}
                onChange={handleChange}
              />
              {fieldErrors.rollNumber && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.rollNumber}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Admission Number</label>
              <Input
                name="admissionNumber"
                value={formData.admissionNumber}
                onChange={handleChange}
              />
              {fieldErrors.admissionNumber && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.admissionNumber}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Aadhaar Card</label>
              <Input
                name="aadhaarCard"
                value={formData.aadhaarCard}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Class</label>
              <Select
                name="class"
                value={formData.class}
                onValueChange={(value) =>
                  handleChange({ target: { name: "class", value } })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Class" />
                </SelectTrigger>
                <SelectContent>
                  {classData.map((cls) => (
                    <SelectItem key={cls._id} value={cls._id}>
                      Class {cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Section</label>
              <Select
                value={formData.section}
                onValueChange={(value) =>
                  handleChange({ target: { name: "section", value } })
                }
                disabled={!formData.class}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      formData.class ? "Select section" : "Select class first"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableSections.map((section) => (
                    <SelectItem key={section._id} value={section._id}>
                      Section {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select
                value={formData.status || "active"}
                onValueChange={(value) =>
                  handleChange({ target: { name: "status", value } })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  {classData.length > 0 && (() => {
                    const sorted = [...classData].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    const highestClassId = sorted[sorted.length - 1]?._id;
                    const isHighestClass = formData.class && String(formData.class) === String(highestClassId);
                    return isHighestClass ? <SelectItem value="alumni">Alumni</SelectItem> : null;
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">BirthDay</label>
              <Input
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                max={new Date().toISOString().split("T")[0]}
              />
              {fieldErrors.dateOfBirth && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.dateOfBirth}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Father's Name</label>
              <Input
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
              />
              {fieldErrors.fatherName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.fatherName}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Father's Contact</label>
              <Input
                name="fatherContactNumber"
                value={formData.fatherContactNumber}
                onChange={handleChange}
              />
              {fieldErrors.fatherContactNumber && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.fatherContactNumber}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Mother's Name</label>
              <Input
                name="motherName"
                value={formData.motherName}
                onChange={handleChange}
              />
              {fieldErrors.motherName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.motherName}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Mother's Contact</label>
              <Input
                name="motherContactNumber"
                value={formData.motherContactNumber}
                onChange={handleChange}
              />
              {fieldErrors.motherContactNumber && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.motherContactNumber}</p>
              )}
            </div>
              </div>

          {/* Blood Group Select */}
              <div>
            <label className="text-sm font-medium">Blood Group</label>
            <Select
              name="bloodGroup"
              value={formData.bloodGroup}
              onValueChange={(value) =>
                handleChange({ target: { name: "bloodGroup", value } })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Blood Group" />
              </SelectTrigger>
              <SelectContent>
                {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                  (group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            {fieldErrors.bloodGroup && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.bloodGroup}</p>
            )}
              </div>

              <div>
            <label className="text-sm font-medium">Gender</label>
            <Select
              name="gender"
              value={formData.gender}
              onValueChange={(value) =>
                handleChange({ target: { name: "gender", value } })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Gender" />
              </SelectTrigger>
              <SelectContent>
                {["Male", "Female", "Other"].map((gender) => (
                  <SelectItem key={gender} value={gender}>
                    {gender}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
              </div>

          {/* Add Transportation Type Select */}
              <div>
            <label className="text-sm font-medium">Transportation Type</label>
            <Select
              name="transportationType"
              value={formData.transportationType}
              onValueChange={(value) =>
                handleChange({ target: { name: "transportationType", value } })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Transportation Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bus">Bus</SelectItem>
                <SelectItem value="van">Van</SelectItem>
                <SelectItem value="self">Self</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
              </div>

          {/* Removed Bus Route select field and all related logic */}

          {/* Address Fields */}
              <div className="space-y-4">
            <h3 className="text-lg font-medium">Address Details</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Street</label>
                <Input
                  name="address.street"
                  value={formData.address?.street}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="text-sm font-medium">City</label>
                <Input
                  name="address.city"
                  value={formData.address?.city}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="text-sm font-medium">State</label>
                <Input
                  name="address.state"
                  value={formData.address?.state}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Pincode</label>
                <Input
                  name="address.pincode"
                  value={formData.address?.pincode}
                  onChange={handleChange}
                />
                {fieldErrors.pincode && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.pincode}</p>
                )}
              </div>
            </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t px-4 py-4 sm:px-6">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;
